import debounce from 'lodash/debounce';
import React, { useEffect, useRef, useState } from 'react';
import { MdAutoAwesome, MdOutlineArrowDownward, MdOutlineArrowUpward, MdOutlineShoppingBag, MdTune } from 'react-icons/md';
import { useIntl } from 'react-intl';

import FramiIconButton from '../common/FramiIconButton';
import {
    useAppContext,
    useComposition,
    useCompositionTemplates,
    useFrami,
    useNavigation,
    useOrder,
    DrawerContext,
} from '../../context';
import {
    CHECKOUT,
    FILTER_DRAWER,
    FRAMESTYLE_DRAWER,
    IMAGE_DRAWER,
    LOGIN,
    ORDER_STATUS,
    TEMPLATE_DRAWER,
    VIDEO_SCREEN,
    WALLSIZE_DRAWER,
} from '../../providers/NavigationProvider';
import AdditionalFilters from './AdditionalFilters';
import CategorySelect from './CategorySelect';
import Dropzone from './Dropzone';
import DrawerHeader from './drawer-content/DrawerHeader';
import { DrawerContentWrapper, DrawerResultsWrapper, Wrapper } from './drawer-content/styled';
import { smallElevationShadow } from '../../common/styles';
import FramiBadge from '../common/FramiBadge';
import { useWorkspace } from '../../context';

const Drawer = () => {
    const { isFramiObsolete } = useFrami();
    const { activeFrame, currentComp } = useComposition();
    const {
        activeDrawer,
        drawerHeight,
        drawerContent,
        drawerHeaderHeight,
        floatingBottomHeight,
        navbarHeight,
        toolbarHeight,
        frameStyleMode,
        setFrameStyleMode,
        closeDrawer,
        openDrawer,
        setDrawerHeight,
    } = useNavigation();
    const { isMobile, isSafari, smallHeightDevice, isHandheldDevice } = useAppContext();
    const { scrollPosition, setScrollPosition } = useCompositionTemplates();
    const { order, activeBasket } = useOrder();
    const { mobileToolbar } = useWorkspace();

    const fullDrawer = [CHECKOUT, VIDEO_SCREEN, LOGIN, ORDER_STATUS].includes(activeDrawer);

    const wrapperRef = useRef(null);
    const scrollRef = useRef(null);
    const [canScroll, setCanScroll] = useState(false);
    const [showArrowUp, setShowArrowUp] = useState(false);
    const [showArrowDown, setShowArrowDown] = useState(false);

    const [drawerTitle, setDrawerTitle] = useState('');
    const [hideButtons, setHideButtons] = useState(false);
    const [drawerOnClick, setDrawerOnClose] = useState(() => () => closeDrawer());
    const [drawerContentHeight, setDrawerContentHeight] = useState(0);
    const [showAdditionalFilters, setShowAdditionalFilters] = useState(true);
    const [tooltipHelper, setTooltipHelper] = useState('');

    const intl = useIntl();

    const getAdditionalButton = () => {
        switch (activeDrawer) {
            case IMAGE_DRAWER:
                if (!activeFrame && !currentComp?.baseline_data) {
                    return {
                        title: intl.formatMessage({ id: 'studio.composition.filter.select.title' }),
                        icon: <MdAutoAwesome size={18} />,
                        dataPh: 'photo filter selection button',
                        onClick: () => {
                            openDrawer(FILTER_DRAWER, false);
                        },
                    };
                }
                break;
            // case FRAMESTYLE_DRAWER:
            //     return {
            //         title: intl.formatMessage({
            //             id:
            //                 frameStyleMode === 'basic'
            //                     ? 'studio.frameStyle.settings.advanced.title'
            //                     : 'studio.frameStyle.settings.basic.title',
            //         }),
            //         icon: <MdTune size={20} />,
            //         dataPh: 'advanced settings switch button',
            //         dataCy: `frame-style-settings-switch-button-${frameStyleMode === 'basic' ? 'advanced' : 'basic'
            //             }`,
            //         onClick: () => {
            //             setFrameStyleMode(frameStyleMode === 'basic' ? 'advanced' : 'basic');
            //         },
            //     };
            //     break;
            default:
                return null;
        }
    };

    const additionalButton = getAdditionalButton();

    useEffect(() => {
        const debouncedSetCanScroll = debounce((canScroll) => {
            setCanScroll(canScroll);
        }, 200);

        const debouncedSetDrawerHeight = debounce((height) => {
            setDrawerHeight(height);
        }, 0);

        const observer = new ResizeObserver((entries) => {
            for (let entry of entries) {
                if (entry.target.className.includes('styled__Wrapper')) {
                    const { height } = entry.contentRect;
                    debouncedSetDrawerHeight(height);
                }
                if (entry.target.className.includes('styled__DrawerContentWrapper')) {
                    const { clientHeight, scrollHeight } = entry.target;
                    debouncedSetCanScroll(scrollHeight > clientHeight);
                }
            }
        });

        if (scrollRef.current) {
            observer.observe(scrollRef.current);
        }

        if (wrapperRef.current) {
            observer.observe(wrapperRef.current);
        } else {
            debouncedSetDrawerHeight(0);
        }

        return () => {
            observer.disconnect();
        };
    }, [activeDrawer, drawerContent, isMobile]);

    useEffect(() => {
        const timer = setTimeout(
            () => {
                const current = scrollRef.current;
                if (current && !isMobile) {
                    setCanScroll(current.scrollHeight > current.clientHeight);
                }
            },
            isSafari ? 1000 : 500
        );

        return () => clearTimeout(timer);
    }, [activeDrawer, drawerHeight, drawerContent, isMobile, isSafari, drawerTitle]);

    useEffect(() => {
        const currentRef = scrollRef.current;

        const handleScroll = () => {
            if (currentRef) {
                if (!isMobile) {
                    setShowArrowDown(currentRef.scrollTop < currentRef.scrollHeight - currentRef.clientHeight - 1);
                    setShowArrowUp(currentRef.scrollTop > 52);
                }

                const newScrollPosition = currentRef.scrollTop;
                setShowAdditionalFilters(
                    newScrollPosition <= (isMobile ? 90 : 80) || newScrollPosition < scrollPosition
                );
                setScrollPosition(newScrollPosition);
            }
        };

        if (currentRef) {
            currentRef.addEventListener('scroll', handleScroll);
        }

        return () => {
            if (currentRef) {
                currentRef.removeEventListener('scroll', handleScroll);
            }
        };
    }, [drawerContent, scrollPosition, isMobile]);

    useEffect(() => {
        setShowArrowUp(false);
        setShowArrowDown(canScroll && !isMobile);
        if ([ORDER_STATUS, VIDEO_SCREEN].includes(activeDrawer)) {
            setDrawerTitle('');
            console.log('Drawer setDrawerTitle: ""');
            setHideButtons(true);
        }
    }, [activeDrawer, canScroll, isMobile]);

    const handleScroll = (direction) => {
        const step = scrollRef.current.clientHeight * 0.8;

        scrollRef.current.scrollBy({ top: direction === 'up' ? -step : step, behavior: 'smooth' });
    };

    useEffect(() => {
        if (activeDrawer === WALLSIZE_DRAWER) {
            setTooltipHelper(intl.formatMessage({ id: 'components.studio.drawer.header.tooltip.wall_size' }));
        } else {
            setTooltipHelper('');
        }
    }, [activeDrawer]);

    useEffect(() => {
        let height;
        if (isMobile) {
            height = `calc(var(--vh, 1vh) * ${fullDrawer ? 100 : 75} - ${navbarHeight + drawerHeaderHeight + floatingBottomHeight
                }px)`;
        } else {
            var toolbar = 0;
            if (mobileToolbar) {
                toolbar = 0;
            }
            else {
                toolbar = toolbarHeight + navbarHeight;
            }
            height = `calc(var(--vh, 1vh) * 97 - ${drawerHeaderHeight + floatingBottomHeight + toolbar
                }px)`;
        }
        setDrawerContentHeight(height);
    }, [activeDrawer, isMobile, drawerHeaderHeight, floatingBottomHeight, navbarHeight, toolbarHeight, mobileToolbar]);

    return (
        drawerContent && (
            <DrawerContext.Provider
                value={{
                    scrollRef,
                    showAdditionalFilters,
                    drawerContentHeight,
                    setCanScroll,
                    setDrawerTitle,
                    setHideButtons,
                    setDrawerOnClose,
                }}
            >
                <Wrapper ref={wrapperRef} $bottomOffset={floatingBottomHeight} $isMobile={isMobile}>
                    <DrawerHeader
                        title={drawerTitle}
                        tooltipHelpTitle={tooltipHelper}
                        hideButton={hideButtons}
                        showSortingOptions={activeDrawer === TEMPLATE_DRAWER}
                        showShadows={scrollRef?.current?.scrollTop > 1}
                        onClose={drawerOnClick}
                        additionalButton={additionalButton}
                    >
                        {activeDrawer === TEMPLATE_DRAWER && !isFramiObsolete && (
                            <>
                                <CategorySelect
                                    filtersVisible={showAdditionalFilters}
                                    scrollPosition={scrollPosition}
                                />
                                <AdditionalFilters showFilters={showAdditionalFilters} />
                            </>
                        )}
                    </DrawerHeader>
                    <DrawerContentWrapper
                        ref={scrollRef}
                        $isMobile={isHandheldDevice}
                        $isSmall={smallHeightDevice}
                        $height={drawerContentHeight}
                        $fullDrawer={fullDrawer ? drawerContentHeight : null}
                        $fixed={activeDrawer === TEMPLATE_DRAWER}
                        className="drawer-scroll-container"
                        data-cy="scroll-box"
                    >
                        <Dropzone noClick={true}>
                            {canScroll && (
                                <>
                                    {showArrowUp && (
                                        <FramiIconButton
                                            style={
                                                activeDrawer === TEMPLATE_DRAWER
                                                    ? {
                                                        top: `${navbarHeight +
                                                            toolbarHeight +
                                                            drawerHeaderHeight +
                                                            (showAdditionalFilters ? 100 : 26)
                                                            }px`,
                                                    }
                                                    : {
                                                        top: `${navbarHeight + toolbarHeight + drawerHeaderHeight + 16
                                                            }px`,
                                                    }
                                            }
                                            className={'scroll-arrow up'}
                                            icon={<MdOutlineArrowUpward size={26} />}
                                            onClick={() => handleScroll('up')}
                                        />
                                    )}
                                    {showArrowDown && (
                                        <FramiIconButton
                                            style={{
                                                bottom: `${floatingBottomHeight + 16}px`,
                                            }}
                                            className={'scroll-arrow down'}
                                            icon={<MdOutlineArrowDownward size={26} />}
                                            onClick={() => handleScroll('down')}
                                        />
                                    )}
                                </>
                            )}
                            <DrawerResultsWrapper
                                $videoDrawer={activeDrawer === VIDEO_SCREEN}
                                $templateDrawer={activeDrawer === TEMPLATE_DRAWER}
                                $isMobile={isMobile}
                                $isSmall={smallHeightDevice}
                            >
                                {drawerContent}
                                {smallHeightDevice && activeBasket && activeDrawer !== CHECKOUT && (
                                    <section
                                        style={{
                                            position: 'absolute',
                                            bottom: floatingBottomHeight + 8,
                                            right: 8,
                                            zIndex: 9,
                                        }}
                                        onClick={() => openDrawer(CHECKOUT)}
                                    >
                                        <FramiBadge
                                            badgeStyle={{
                                                right: 19,
                                                top: 27,
                                                scale: '60%',
                                            }}
                                            badgeContent={order?.compositions?.length}
                                        >
                                            <FramiIconButton
                                                style={{ boxShadow: smallElevationShadow }}
                                                className={`workspace-button ${isMobile ? 'mobile' : ''}`}
                                                size={40}
                                                icon={<MdOutlineShoppingBag />}
                                            />
                                        </FramiBadge>
                                    </section>
                                )}
                            </DrawerResultsWrapper>
                        </Dropzone>
                    </DrawerContentWrapper>
                </Wrapper>
            </DrawerContext.Provider>
        )
    );
};

export default Drawer;
